import React, { useState, useMemo } from 'react';
import { Button } from '../../../../components/ui';
import { FiPlus as Plus, FiUserPlus as UserPlus, FiUsers as UsersIcon, FiBriefcase as BriefcaseIcon, FiEdit2 as Edit, FiTrash2 as Trash, FiShield as Shield, FiAward as Crown } from 'react-icons/fi';
import { useAuth } from '../../../../contexts/AuthContext';
import { usePermissions } from '../../../../hooks/usePermissions';
import { PERMISSIONS } from '../../../../utils/permissions';
import { apiCall } from '../../../../services/realApi';
import AddUserModal from './AddUserModal';
import InviteUserModal from './InviteUserModal';
import EditUserModal from './EditUserModal';
import TransferOwnershipModal from './TransferOwnershipModal';

export interface UserRecord {
  id: string;
  user_id?: string;
  name?: string;
  email: string;
  role?: string;
  clientRole?: string;
  client_role?: string;
  canManageUsers?: boolean;
  can_manage_users?: boolean;
  organization?: string;
  last_active?: string;
  last_login?: string;
  lastLogin?: string;
}

interface UsersTabProps {
  clientUsers?: UserRecord[];
  partnerUsers?: UserRecord[];
  loading: boolean;
  onRefresh?: () => void;
  showSelectClientPrompt?: boolean;
  canViewPartnerUsers?: boolean;
  scope?: string;
}

const UsersTab: React.FC<UsersTabProps> = ({ clientUsers = [], partnerUsers = [], loading, onRefresh, showSelectClientPrompt = false, canViewPartnerUsers = false, scope = 'all' }) => {
  const { user: currentUser } = useAuth();
  const { hasPermission } = usePermissions();
  const [isAddClientUserModalOpen, setIsAddClientUserModalOpen] = useState(false);
  const [isAddPartnerUserModalOpen, setIsAddPartnerUserModalOpen] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserRecord | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);

  // Check if user can invite AWS users (partner/client users can invite)
  const canInviteAWSUser = useMemo(() => {
    if (!currentUser) return false;
    return (
      hasPermission(PERMISSIONS.USERS_CREATE) ||
      hasPermission(PERMISSIONS.USERS_MANAGE) ||
      hasPermission(PERMISSIONS.AWS_ACCESS_MANAGE)
    );
  }, [currentUser, hasPermission]);

  // Check if user can add partner users (partners with users.create permission)
  const canAddPartnerUsers = useMemo(() => {
    if (!currentUser) return false;
    return (
      hasPermission(PERMISSIONS.USERS_CREATE) &&
      hasPermission(PERMISSIONS.PARTNERS_MANAGE)
    );
  }, [currentUser, hasPermission]);

  // Check if current user can manage org users (add/edit/delete)
  const canManageOrgUsers = useMemo(() => {
    if (!currentUser) return false;
    // Check RBAC permission or can_manage_users flag from user object
    return (
      hasPermission(PERMISSIONS.USERS_CREATE) ||
      currentUser.can_manage_users ||
      currentUser.canManageUsers ||
      currentUser.client_role === 'account_owner' ||
      currentUser.client_role === 'admin_user'
    );
  }, [currentUser, hasPermission]);

  // Check if current user is the account owner
  const isCurrentUserAccountOwner = useMemo(() => {
    if (!currentUser) return false;
    return currentUser.client_role === 'account_owner';
  }, [currentUser]);

  const getRoleBadgeColor = (role: string | undefined) => {
    switch (role?.toLowerCase()) {
      case 'partner':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300';
      case 'client':
        return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300';
      case 'aws':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300';
      case 'aivilo_staff':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getClientRoleBadge = (user: UserRecord) => {
    const clientRole = (user.clientRole || user.client_role)?.toLowerCase();
    if (user.role?.toLowerCase() !== 'client' || !clientRole) {
      return null;
    }

    switch (clientRole) {
      case 'account_owner':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300">
            <Crown className="w-3 h-3 mr-1" />
            Account Owner
          </span>
        );
      case 'admin_user':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300">
            <Shield className="w-3 h-3 mr-1" />
            Admin User
          </span>
        );
      case 'regular_user':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
            Regular User
          </span>
        );
      default:
        return <span className="text-xs text-gray-400">—</span>;
    }
  };

  const userCanManage = (user: UserRecord) => {
    const clientRole = user.clientRole || user.client_role;
    return user.canManageUsers || user.can_manage_users || clientRole === 'account_owner' || clientRole === 'admin_user';
  };

  const getInitials = (name: string | undefined, email: string | undefined) => {
    if (name) {
      const parts = name.split(' ');
      if (parts.length > 1) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    }
    if (email) {
      return email.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  // Get partner name for the Partner Users table title (must be before early returns)
  const partnerName = useMemo(() => {
    if (partnerUsers.length > 0 && partnerUsers[0].organization) {
      return partnerUsers[0].organization;
    }
    return 'Partner';
  }, [partnerUsers]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const handleAddSuccess = () => {
    if (onRefresh) {
      onRefresh();
    }
  };

  const handleInviteSuccess = () => {
    setShowInviteModal(false);
    if (onRefresh) {
      onRefresh();
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!globalThis.confirm('Are you sure you want to delete this user?')) return;
    try {
      await apiCall(`/users/${userId}`, { method: 'DELETE' });
      if (onRefresh) {
        onRefresh();
      }
    } catch (err: unknown) {
      console.error('Error deleting user:', err);
      alert((err instanceof Error ? err.message : null) || 'Failed to delete user');
    }
  };

  const handleEditUser = (user: UserRecord) => {
    setEditingUser(user);
    setIsEditModalOpen(true);
  };

  const handleEditSuccess = () => {
    if (onRefresh) {
      onRefresh();
    }
  };

  const handleTransferSuccess = () => {
    if (onRefresh) {
      onRefresh();
    }
  };

  // Render a user table
  const renderUserTable = (users: UserRecord[], _title: string, emptyMessage: string, avatarColor = 'bg-blue-500', showClientRole = false) => {
    const showActions = showClientRole && canManageOrgUsers;
    const colCount = 3 + (showClientRole ? 1 : 0) + (showActions ? 1 : 0);

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700 transition-colors">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                USER
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                ROLE
              </th>
              {showClientRole && (
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  CLIENT ROLE
                </th>
              )}
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                LAST ACTIVE
              </th>
              {showActions && (
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  ACTIONS
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700 transition-colors">
            {users.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              users.map((user) => {
                const userClientRole = (user.clientRole || user.client_role)?.toLowerCase();
                const isAccountOwner = userClientRole === 'account_owner';
                const canDelete = !isAccountOwner && (user.id !== currentUser?.id);
                const canEdit = !isAccountOwner && (user.id !== currentUser?.id);

                return (
                  <tr key={user.id || user.user_id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className={`h-10 w-10 rounded-full ${avatarColor} flex items-center justify-center text-white text-sm font-medium`}>
                            {getInitials(user.name, user.email)}
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white transition-colors">
                            {user.name || 'Unknown User'}
                            {userCanManage(user) && (
                              <Shield className="inline w-3 h-3 ml-1.5 text-blue-500 dark:text-blue-400" title="Can manage users" />
                            )}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400 transition-colors">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2.5 py-0.5 inline-flex text-xs font-medium rounded-full ${getRoleBadgeColor(user.role)}`}>
                        {user.role?.toUpperCase() || 'N/A'}
                      </span>
                    </td>
                    {showClientRole && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getClientRoleBadge(user)}
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 transition-colors">
                      {formatDate(user.last_active || user.last_login || user.lastLogin)}
                    </td>
                    {showActions && (
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => canEdit && handleEditUser(user)}
                            disabled={!canEdit}
                            className={`transition-colors p-1 rounded ${
                              canEdit
                                ? 'text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                                : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                            }`}
                            title={canEdit ? 'Edit user role' : (isAccountOwner ? 'Cannot edit Account Owner role' : 'Cannot edit yourself')}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => canDelete && handleDeleteUser(user.id)}
                            disabled={!canDelete}
                            className={`transition-colors p-1 rounded ${
                              canDelete
                                ? 'text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20'
                                : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                            }`}
                            title={canDelete ? 'Delete user' : isAccountOwner ? 'Cannot delete Account Owner - transfer ownership first' : 'Cannot delete yourself'}
                          >
                            <Trash className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const showClientSection = scope === 'all' || scope === 'client';
  const showPartnerSection = scope === 'all' || scope === 'partner';

  return (
    <div className="space-y-6">
      {/* Client Users Table */}
      {showClientSection && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden transition-colors">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <UsersIcon className="w-5 h-5 text-green-500" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white transition-colors">
                Client Users
              </h2>
              <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                {clientUsers.length}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              {/* Transfer Ownership button - only visible to Account Owner */}
              {isCurrentUserAccountOwner && (
                <Button
                  variant="secondary"
                  onClick={() => setIsTransferModalOpen(true)}
                  className="flex items-center space-x-2"
                >
                  <Crown className="w-4 h-4" />
                  <span>Transfer Ownership</span>
                </Button>
              )}
              {/* Invite AWS User button for permitted users */}
              {canInviteAWSUser && canManageOrgUsers && (
                <Button
                  variant="primary"
                  onClick={() => setShowInviteModal(true)}
                  className="flex items-center space-x-2"
                >
                  <UserPlus className="w-4 h-4" />
                  <span>Invite AWS User</span>
                </Button>
              )}
              {canManageOrgUsers && (
                <Button
                  variant="primary"
                  onClick={() => setIsAddClientUserModalOpen(true)}
                  className="flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add User</span>
                </Button>
              )}
            </div>
          </div>
          {showSelectClientPrompt ? (
            <div className="p-12 text-center">
              <UsersIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Please select a client to view users
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                Use the client selector in the navigation bar to choose a client.
              </p>
            </div>
          ) : (
            renderUserTable(clientUsers, 'Client Users', 'No client users found', 'bg-green-500', true)
          )}
        </div>
      )}

      {/* Partner Users Table - Only visible to partners and aivilo_staff */}
      {showPartnerSection && canViewPartnerUsers && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden transition-colors">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <BriefcaseIcon className="w-5 h-5 text-blue-500" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white transition-colors">
                {partnerName} Users
              </h2>
              <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                {partnerUsers.length}
              </span>
            </div>
            {canAddPartnerUsers && (
              <Button
                variant="primary"
                onClick={() => setIsAddPartnerUserModalOpen(true)}
                className="flex items-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>Add {partnerName} User</span>
              </Button>
            )}
          </div>
          {renderUserTable(partnerUsers, `${partnerName} Users`, `No ${partnerName.toLowerCase()} users found`, 'bg-blue-500')}
        </div>
      )}

      {/* Add Client User Modal */}
      <AddUserModal
        isOpen={isAddClientUserModalOpen}
        onClose={() => setIsAddClientUserModalOpen(false)}
        onSuccess={handleAddSuccess}
        defaultRole="client"
      />

      {/* Add Partner User Modal */}
      <AddUserModal
        isOpen={isAddPartnerUserModalOpen}
        onClose={() => setIsAddPartnerUserModalOpen(false)}
        onSuccess={handleAddSuccess}
        defaultRole="partner"
      />

      {/* Edit User Modal */}
      <EditUserModal
        isOpen={isEditModalOpen}
        onClose={() => { setIsEditModalOpen(false); setEditingUser(null); }}
        onSuccess={handleEditSuccess}
        user={editingUser}
      />

      {/* Transfer Ownership Modal */}
      {isCurrentUserAccountOwner && (
        <TransferOwnershipModal
          isOpen={isTransferModalOpen}
          onClose={() => setIsTransferModalOpen(false)}
          onSuccess={handleTransferSuccess}
          currentOwnerId={currentUser?.id || currentUser?.user_id}
        />
      )}

      {/* Invite User Modal for Partner/Client Users */}
      {canInviteAWSUser && (
        <InviteUserModal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          onSuccess={handleInviteSuccess}
          allUsers={[]}
          inviteContext={null}
          currentUserId={currentUser?.id || currentUser?.user_id}
        />
      )}
    </div>
  );
};

export default UsersTab;
